import React, { useContext } from 'react';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { string, number } from 'prop-types';
import { useStore, useConfigService } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AppContext } from '@thd-nucleus/app-render'
import { useLocation } from '@thd-olt-component-react/router';
import { BrowseAndSearch } from './BrowseAndSearch';
import { ONLINE_STORE_ID } from '../../constants';
import {
  getContentfulPreviewCustomerType,
  getContentfulPreviewPathName
} from './browse-and-search-utils';

export const BrowseAndSearchContainer = (props) => {
  const { isCustomerIdentified = false } = useThdCustomer() || {};
  const { pageType, searchTimeout } = props;
  const isSearch = pageType === 'search';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const { membershipInformation, storeId, storeZip, isLocalized } = store;
  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  const isBuyitagainEnabled = useConfigService('fs-prop:isBuyitagainEnabled');
  const { pathname } = useLocation();
  const { instance } = useContext(AppContext);
  let customerType = instance?.customer?.type;

  // --this is specific for being able to render pages for Contentful's previews
  const previewLink = instance?.headers?.['x-content-preview'];
  let finalPathname = pathname;
  if (previewLink) {
    finalPathname = getContentfulPreviewPathName(pathname);
    customerType = getContentfulPreviewCustomerType(pathname);
  }

  const searchOptsFn = ({ options, queryName }) => {
    if (queryName === 'searchModel') {
      let to = 3000;
      if (searchTimeout) {
        if (typeof searchTimeout === 'string') {
          to = parseInt(searchTimeout, 10);
          // eslint-disable-next-line no-restricted-globals
          if (isNaN(to)) {
            to = 3000;
          }
        } else if (typeof searchTimeout === 'number') {
          to = searchTimeout;
        }
      }

      // eslint-disable-next-line no-param-reassign
      options.context = {
        ...(options.context || {}),
        timeout: to
      }
    }

    return options;
  };

  const isMounted = ({ mounted }) => {
    if (storeId === ONLINE_STORE_ID && mounted) {
      return false;
    }

    if (membershipInformation?.error) {
      return mounted;
    }

    if (membershipInformation?.loading) {
      return false;
    }

    return mounted;
  }

  const browseDefaultVariables = {
    skipInstallServices: false,
    skipSpecificationGroup: false,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership,
    skipDiscoveryZones: !(isDiscoveryZonesEnabled && pageType === 'browse')
    && !(isDiscoveryZonesSearchEnabled && pageType === 'search'),
    skipBuyitagain: !isBuyitagainEnabled,
    isBrandPricingPolicyCompliant: isLocalized ? isCustomerIdentified : false
  };

  if (browseDefaultVariables) {
    browseDefaultVariables.skipBuyitagain = !isBuyitagainEnabled;
  }

  if (pageType === 'browse') {
    return (
      <QueryProvider
        skip={({ skip }) => skip}
        queryOptions={searchOptsFn}
        defaultVariables={browseDefaultVariables}
        mounted={isMounted}
        cacheKey="browse-search--browse"
      >
        <BrowseAndSearch
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...props}
          pathname={finalPathname}
          customerType={customerType}
          instance={instance}
          isLocalized={isLocalized}
          storeId={storeId}
          storeZip={storeZip}
        />
      </QueryProvider>
    )
  }

  return (
    <QueryProvider
      queryOptions={searchOptsFn}
      defaultVariables={browseDefaultVariables}
      mounted={isMounted}
      cacheKey="browse-search--typeBrowse"
      forceClientQueriesOnServer={['searchModel', 'product']}
    >
      <BrowseAndSearch
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
        pathname={finalPathname}
        isLocalized={isLocalized}
        storeId={storeId}
        storeZip={storeZip}
      />
    </QueryProvider>
  )

};

BrowseAndSearchContainer.propTypes = {
  pageType: string.isRequired,
  searchTimeout: string,
  productPodHoverDelay: number
};

BrowseAndSearchContainer.defaultProps = {
  searchTimeout: null,
  productPodHoverDelay: 500
};
